import { useMemo, type ReactNode } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { useCreatorAccess } from "@/hooks/useCreatorAccess";
import { useProgramAuthorAccess } from "@/hooks/useProgramAuthorAccess";
import { supabase } from "@/integrations/supabase/client";
import { LoadingState } from "@/components/feedback/LoadingState";
import { getJourneyAuthorReturn } from "@/utils/courseEditorDraft";
import {
  normalizePreviewProgram,
  readAuthorSnapshot,
  PREVIEW_UUID,
} from "@/utils/authorPreview";
import {
  WritingPreviewProvider,
  type WritingPreviewPrompt,
} from "./WritingPreviewContext";

/** Private content is read through RLS, then bound to this exact owner/admin and route. */
export function ProgramPreviewBoundary({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const access = useCreatorAccess();
  const author = useProgramAuthorAccess();
  const { programId } = useParams();
  const [params] = useSearchParams();
  const token = params.get("previewSnapshot");
  const value = useMemo(
    () => readAuthorSnapshot(user?.id, token),
    [user?.id, token],
  );
  const snapshot = value?.kind === "program" ? value : null;
  const valid =
    params.get("preview") === "1" &&
    params.get("previewProgram") === "1" &&
    !params.has("journeyId") &&
    Boolean(programId && PREVIEW_UUID.test(programId)) &&
    (!token || snapshot?.program.id === programId);
  const sourceId = token ? snapshot?.sourceId : programId;
  const source = useQuery({
    queryKey: ["program-author-preview", user?.id, sourceId],
    enabled: Boolean(valid && user && sourceId),
    retry: false,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .eq("id", sourceId!)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
  const authorized = Boolean(
    user &&
    (sourceId
      ? source.data &&
        (source.data.created_by === user.id || access.data?.isAdmin === true) &&
        ["powerprompt", "powerPrompt"].includes(source.data.program_type || "")
      : snapshot && author.hasPremiumAccess),
  );
  const prompts = useQuery({
    queryKey: ["program-author-preview-prompts", user?.id, sourceId],
    enabled: Boolean(valid && authorized && sourceId && !snapshot),
    retry: false,
    staleTime: 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("prompts")
        .select("id, title, content, duration, order_index")
        .eq("program_id", sourceId!)
        .order("order_index");
      if (error) throw error;
      return (data || []) as WritingPreviewPrompt[];
    },
  });
  if (
    loading ||
    access.isLoading ||
    (!sourceId && author.isLoadingSubscription) ||
    (source.isLoading && source.fetchStatus !== "idle") ||
    (prompts.isLoading && prompts.fetchStatus !== "idle")
  )
    return <LoadingState variant="page" />;
  if (source.isError || access.isError || prompts.isError)
    return (
      <div role="alert" className="mx-auto max-w-xl p-8">
        Your preview couldn't load.{" "}
        <button
          className="underline"
          onClick={() => {
            void source.refetch();
            void access.refetch();
            void prompts.refetch();
          }}
        >
          Try again
        </button>
      </div>
    );
  if (!valid || !authorized || !user)
    return (
      <div role="alert" className="mx-auto max-w-xl p-8">
        This preview isn't available for your account.{" "}
        <Link to="/my-programs" className="underline">
          Back to my programs
        </Link>
      </div>
    );
  const from =
    (snapshot?.from || params.get("previewFrom")) === "admin" &&
    access.data?.isAdmin === true
      ? "admin"
      : "creator";
  const returnParams = new URLSearchParams();
  if (snapshot) returnParams.set("restorePreview", snapshot.token);
  const editorState = snapshot?.editorState as
    | { activeTab?: string }
    | undefined;
  if (
    from === "admin" &&
    ["details", "prompts", "landing"].includes(
      editorState?.activeTab || "",
    )
  )
    returnParams.set("tab", editorState!.activeTab!);
  const safeJourneyReturn = snapshot?.journeyReturn
    ? getJourneyAuthorReturn(
        new URLSearchParams({ journeyReturn: snapshot.journeyReturn }),
      )
    : null;
  if (safeJourneyReturn) returnParams.set("journeyReturn", safeJourneyReturn);
  const path =
    params.get("previewList") === "1"
      ? from === "admin"
        ? "/admin/programs"
        : "/my-programs"
      : from === "admin"
        ? `/admin/programs/${sourceId || "new"}`
        : `/create/program${sourceId ? `/${sourceId}` : ""}${snapshot?.editor === "prompts" ? "/prompts" : ""}`;
  const returnPath = `${path}${returnParams.size ? `?${returnParams}` : ""}`;
  const program =
    snapshot?.program || normalizePreviewProgram(source.data || {}, programId!);
  return (
    <WritingPreviewProvider
      value={{
        completeWriting: () => {},
        returnPath,
        isUnsaved: Boolean(snapshot),
        previewKey: `${user.id}:${programId}:${token || "saved"}`,
        programOverride: program,
        promptsOverride: snapshot?.prompts || prompts.data || [],
        completionImageUrl: null,
      }}
    >
      {children}
    </WritingPreviewProvider>
  );
}
